/**
 * Servicio de autenticación para operaciones de perfil de usuario
 * Centraliza las llamadas a Supabase para autenticación
 */

import { supabase } from '../lib/supabase';

/**
 * Verifica la contraseña del usuario usando RPC de Supabase
 * @param {string} password - Contraseña a verificar
 * @returns {Promise<boolean>} - True si la contraseña es correcta
 * @throws {Error} - Si la contraseña es incorrecta o hay error en la llamada
 */
export const verifyUserPassword = async (password) => {
  try {
    const { data, error } = await supabase.rpc("verify_user_password", { 
      password 
    });

    if (error) {
      console.error('Error en verifyUserPassword:', error);
      throw new Error(error.message);
    }

    // El RPC devuelve un array con el resultado en data[0]?.payload
    console.log(' --- verifyUserPassword(data)', data[0]?.payload);

    // Si la contraseña es incorrecta, lanzar un error para que TanStack lo maneje como tal
    if (!data || data[0]?.payload !== true) {
      throw new Error('Contraseña incorrecta');
    }

    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Verifica si un email ya está en uso usando RPC de Supabase
 * @param {string} email - Email a verificar
 * @returns {Promise<boolean>} - True si el email ya está registrado
 */
export const verifyUserEmail = async (email) => {
  try {
    const { data, error } = await supabase.rpc("verify_user_email", { 
      email_address: email 
    });

    if (error) {
      console.error('Error en verifyUserEmail:', error);
      throw new Error(error.message);
    }

    // El RPC devuelve un array con el resultado en data[0]?.payload
    // Retorna true si el email ya está registrado
    return data && data[0]?.payload === true;
  } catch (error) {
    console.error('Error verificando email:', error);
    throw error;
  }
};

/**
 * Actualiza el email del usuario
 * @param {string} newEmail - Nuevo email
 * @returns {Promise<object>} - Resultado de la operación
 */
export const updateUserEmail = async (newEmail) => {
  try {
    const { data, error } = await supabase.auth.updateUser({ 
      email: newEmail 
    });

    if (error) {
      console.error('Error en updateUserEmail:', error);
      throw new Error(error.message);
    }

    return { data, success: true };
  } catch (error) {
    console.error('Error actualizando email:', error);
    throw error;
  }
};

/**
 * Actualiza la contraseña del usuario
 * @param {string} newPassword - Nueva contraseña
 * @returns {Promise<object>} - Resultado de la operación
 */
export const updateUserPassword = async (newPassword) => {
  try {
    const { data, error } = await supabase.auth.updateUser({ 
      password: newPassword 
    });

    if (error) {
      console.error('Error en updateUserPassword:', error);
      throw new Error(error.message);
    }

    return { data, success: true };
  } catch (error) {
    console.error('Error actualizando contraseña:', error);
    throw error;
  }
};

/**
 * Desactiva la cuenta del usuario usando RPC de Supabase
 * @param {object} session - Sesión actual del usuario
 * @returns {Promise<object>} - Resultado de la operación
 */
export const deactivateUserAccount = async (session) => {
  try {
    const { data, error } = await supabase.rpc('deactivate_my_account', { 
      session 
    });

    if (error) {
      console.error('Error en deactivateUserAccount:', error);
      throw new Error(error.message);
    }

    return { data, success: true };
  } catch (error) {
    console.error('Error desactivando cuenta:', error);
    throw error;
  }
};

/**
 * Obtiene la información del usuario actual
 * @returns {Promise<object>} - Información del usuario
 */
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();

    if (error) {
      console.error('Error en getCurrentUser:', error);
      throw new Error(error.message);
    }

    return data?.user || null;
  } catch (error) {
    console.error('Error obteniendo usuario actual:', error);
    throw error;
  }
};

/**
 * Cierra la sesión del usuario
 * @returns {Promise<object>} - Resultado de la operación
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error('Error en signOut:', error);
      throw new Error(error.message);
    }

    return { success: true };
  } catch (error) {
    console.error('Error cerrando sesión:', error);
    throw error;
  }
};

/**
 * Envía email de recuperación de contraseña usando Supabase Auth
 * Usa resetPasswordForEmail que envía un email con el link de recuperación
 * El template del email se puede personalizar en el dashboard de Supabase
 * @param {string} email - Email del usuario
 * @returns {Promise<object>} - Resultado de la operación
 */
export const sendPasswordResetEmail = async (email) => {
  try {
    // Envía email de recuperación usando Supabase Auth
    // El email contiene un link con el token para restablecer la contraseña
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      console.error('Error enviando email de recuperación:', error);
      throw new Error(error.message);
    }

    return { success: true };
  } catch (error) {
    console.error('Error en sendPasswordResetEmail:', error);
    throw error;
  }
};
